import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Coins, Banknote, RefreshCw, Landmark, Copy, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCasinoBalance, useCargar, useRetirar, useRegalar, useRegisterCasino } from '@/hooks/useCasino';
import { hgcashApi, type HgCheckoutResult } from '@/api/hgcash.api';
import { formatCurrency } from '@/lib/utils';

const QUICK = [5000, 10000, 20000, 50000, 100000];

/** Etiquetas amigables para las keys del accountDisplay de HG Cash (objeto libre). */
function accLabel(key: string): string {
  if (/cvu|cbu/i.test(key)) return '🏦 CVU';
  if (/alias/i.test(key)) return '🔖 Alias';
  if (/holder|titular|name/i.test(key)) return '👤 Titular';
  return key;
}

export function CasinoPanel({ clientId }: { clientId: string }) {
  const { data: bal, isLoading, isFetching, refetch } = useCasinoBalance(clientId);
  const cargar = useCargar();
  const retirar = useRetirar();
  const regalar = useRegalar();
  const register = useRegisterCasino();
  const [mode, setMode] = useState<null | 'in' | 'out' | 'hg' | 'gift'>(null);
  const [amount, setAmount] = useState('');
  const [hgResult, setHgResult] = useState<HgCheckoutResult | null>(null);
  // Key de idempotencia del diálogo "Regalar": se genera UNA vez al abrirse
  // (ver onClick del botón Gift más abajo). Un retry/doble-click del MISMO
  // diálogo reusa esta ref → el backend dedupe. Reabrir el diálogo genera
  // una key nueva (permite un regalo genuino nuevo).
  const giftKeyRef = useRef<string | null>(null);

  const { data: hgConfig } = useQuery({
    queryKey: ['hgcash', 'config'],
    queryFn: hgcashApi.config,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const hgCheckout = useMutation({
    mutationFn: (n: number) => hgcashApi.checkout({ clientId, amount: n }),
    onSuccess: (res) => {
      setMode(null);
      setAmount('');
      setHgResult(res);
      toast.success('CVU generado — el jugador ya lo tiene en el chat');
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || 'No se pudo generar el cobro');
    },
  });

  const pending = cargar.isPending || retirar.isPending || regalar.isPending || hgCheckout.isPending;
  const found = bal?.found ?? false;
  const currency = bal?.currency || 'PYG';

  const submit = async () => {
    const n = Number(amount);
    if (!n || n <= 0) {
      toast.error('Monto inválido');
      return;
    }
    if (mode === 'hg') {
      hgCheckout.mutate(n);
      return;
    }
    try {
      if (mode === 'in') await cargar.mutateAsync({ clientId, amount: n });
      else if (mode === 'out') await retirar.mutateAsync({ clientId, amount: n });
      else if (mode === 'gift') {
        // Debería existir siempre (se genera al abrir el diálogo), pero si por
        // lo que sea no está, se genera acá como último recurso.
        if (!giftKeyRef.current) giftKeyRef.current = crypto.randomUUID();
        await regalar.mutateAsync({ clientId, amount: n, idempotencyKey: giftKeyRef.current });
      }
      setMode(null);
      setAmount('');
      giftKeyRef.current = null;
    } catch {
      /* el hook ya muestra el toast de error */
    }
  };

  const hgAccountLines: Array<[string, string]> = hgResult?.accountDisplay
    ? Object.entries(hgResult.accountDisplay)
        .filter(([, v]) => v !== null && v !== '' && typeof v !== 'object')
        .map(([k, v]) => [accLabel(k), String(v)])
    : [];

  const copyHgData = () => {
    const text = [
      ...hgAccountLines.map(([k, v]) => `${k}: ${v}`),
      hgResult?.checkoutUrl ?? '',
    ]
      .filter(Boolean)
      .join('\n');
    void navigator.clipboard.writeText(text);
    toast.success('Datos copiados');
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Casino
        </div>
        <button
          onClick={() => refetch()}
          className="text-muted-foreground transition-colors hover:text-foreground"
          title="Actualizar saldo"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div>
        <div className="text-xs text-muted-foreground">Saldo casino</div>
        <div className="text-lg font-bold">
          {isLoading ? '…' : found ? formatCurrency(bal!.balance, currency) : '—'}
        </div>
        {bal && !found && (
          <div className="text-[10px] text-amber-500">No pertenece a esta caja</div>
        )}
      </div>

      {found ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              onClick={() => {
                setMode('in');
                setAmount('');
              }}
            >
              <Coins className="mr-1 h-3.5 w-3.5" />
              Cargar
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                setMode('out');
                setAmount('');
              }}
            >
              <Banknote className="mr-1 h-3.5 w-3.5" />
              Retirar
            </Button>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => {
              setMode('gift');
              setAmount('');
              giftKeyRef.current = crypto.randomUUID();
            }}
          >
            <Gift className="mr-1 h-3.5 w-3.5" />
            Regalar (bono 200%)
          </Button>
          {hgConfig?.enabled && (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => {
                setMode('hg');
                setAmount('');
              }}
            >
              <Landmark className="mr-1 h-3.5 w-3.5" />
              CVU de carga (HG Cash)
            </Button>
          )}
        </div>
      ) : (
        <Button
          size="sm"
          className="w-full"
          onClick={() => register.mutate(clientId)}
          disabled={register.isPending || isLoading}
        >
          {register.isPending ? 'Creando…' : 'Crear en casino'}
        </Button>
      )}

      <Dialog
        open={mode !== null}
        onOpenChange={(o) => {
          if (!o) {
            setMode(null);
            giftKeyRef.current = null;
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {mode === 'in'
                ? 'Cargar saldo'
                : mode === 'out'
                  ? 'Retirar saldo'
                  : mode === 'gift'
                    ? '🎁 Regalar fichas (bono 200%)'
                    : 'Generar CVU de carga'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {mode === 'hg' && (
              <p className="text-xs text-muted-foreground">
                Crea un cobro en HG Cash y le manda el CVU + alias al jugador por el chat.
                Cuando transfiera el monto EXACTO, el saldo se acredita solo.
              </p>
            )}
            {mode === 'gift' && (
              <p className="text-xs text-muted-foreground">
                Acredita un bono jugable SOLO en el casino (Central) por el monto indicado,
                con rollover x2 (debe jugar 2 veces el monto regalado antes de poder retirarlo).
                No es una carga de saldo real.
              </p>
            )}
            <Input
              type="number"
              min="0"
              placeholder={`Monto (${mode === 'hg' ? 'ARS' : currency})`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
            <div className="flex flex-wrap gap-2">
              {QUICK.map((q) => (
                <Button key={q} size="sm" variant="outline" onClick={() => setAmount(String(q))}>
                  {formatCurrency(q, mode === 'hg' ? 'ARS' : currency)}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => void submit()}
              disabled={pending || !amount}
              variant={mode === 'out' ? 'destructive' : 'default'}
            >
              {pending
                ? 'Procesando…'
                : mode === 'in'
                  ? 'Confirmar carga'
                  : mode === 'out'
                    ? 'Confirmar retiro'
                    : mode === 'gift'
                      ? 'Confirmar regalo'
                      : 'Generar cobro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resultado del checkout HG: CVU/alias para compartir */}
      <Dialog open={hgResult !== null} onOpenChange={(o) => !o && setHgResult(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>💰 Cobro generado</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {hgAccountLines.length > 0 ? (
              hgAccountLines.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-mono text-xs break-all">{v}</span>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground">Sin datos de cuenta — usar el link.</p>
            )}
            {hgResult?.checkoutUrl && (
              <a
                href={hgResult.checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block break-all text-xs text-primary underline"
              >
                {hgResult.checkoutUrl}
              </a>
            )}
            <p className="text-[11px] text-muted-foreground">
              ✅ El jugador ya recibió estos datos en el chat. Al acreditarse el pago, la carga es automática.
            </p>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={copyHgData}>
              <Copy className="mr-1 h-3.5 w-3.5" />
              Copiar datos
            </Button>
            <Button size="sm" onClick={() => setHgResult(null)}>
              Listo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
